import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
  useReducer,
  createContext,
} from 'react';
import { useCart } from '@adapters/bigcommerce/hooks/cart';
import {
  Cart,
  CartLineItem,
  CartMessage,
  CartShipToHomeType,
} from '@adapters/bigcommerce/types/cart';
import { getCartShipToHomeType } from '@utils/cart/checkBikeItems';

export interface State {
  cartIsUpdating: boolean;
  cart: Cart | null;
  totalQuantity: number;
  isDigitalOnly: boolean;
  cartLoadedInitial: boolean;
  deliveryOption: string;
  showSPUCartError: number | null;
  cartShipToHomeType: CartShipToHomeType;
}

export interface Actions {
  setCartIsUpdating: () => void;
  setCartDoneUpdating: () => void;
  updateCart: (cart: Cart | null) => void;
  validateCartItems: (force?: boolean) => void;
  clearCartMessaging: () => void;
  setDeliveryOption: (deliveryOption: string) => void;
  setShowSPUCartError: (showSPUCartError: number | null) => void;
}

export type CartState = State & Actions;

enum ActionType {
  CART_IS_UPDATING = 'CART_IS_UPDATING',
  CART_DONE_UPDATING = 'CART_DONE_UPDATING',
  UPDATE_CART = 'UPDATE_CART',
  FUNCTIONAL_UPDATE_CART = 'FUNCTIONAL_UPDATE_CART',
  SET_DELIVERY_OPTION = 'SET_DELIVERY_OPTION',
  SHOW_SPU_CART_ERROR = 'SHOW_SPU_CART_ERROR',
  SET_ALGOLIA_QUERY_ID = 'SET_ALGOLIA_QUERY_ID',
}

type Action =
  | { type: ActionType.CART_IS_UPDATING }
  | { type: ActionType.CART_DONE_UPDATING }
  | { type: ActionType.UPDATE_CART; cart: Cart | null }
  | { type: ActionType.FUNCTIONAL_UPDATE_CART; cart: Cart | null }
  | { type: ActionType.SET_DELIVERY_OPTION; deliveryOption: string }
  | { type: ActionType.SHOW_SPU_CART_ERROR; showSPUCartError: number | null };

const initialState = {
  cartIsUpdating: false,
  cart: null,
  totalQuantity: 0,
  isDigitalOnly: false,
  isEmpty: true,
  cartLoadedInitial: false,
  deliveryOption: 'shipping',
  showSPUCartError: null,
  cartShipToHomeType: CartShipToHomeType.REGULAR,
  setCartIsUpdating: () => null,
  setCartDoneUpdating: () => null,
  updateCart: () => null,
  validateCartItems: () => null,
  clearCartMessaging: () => null,
  setDeliveryOption: () => null,
  setShowSPUCartError: () => null,
};

export const CartContext = createContext<CartState>(initialState);

CartContext.displayName = 'CartContext';

const cartReducer = (state: State, action: Action) => {
  switch (action.type) {
    case ActionType.CART_IS_UPDATING: {
      return {
        ...state,
        cartIsUpdating: true,
      };
    }
    case ActionType.CART_DONE_UPDATING: {
      return {
        ...state,
        cartIsUpdating: false,
      };
    }
    case ActionType.UPDATE_CART: {
      return {
        ...state,
        cart: action.cart,
        cartLoadedInitial: true,
      };
    }
    case ActionType.FUNCTIONAL_UPDATE_CART: {
      return {
        ...state,
        cart: action.cart,
      };
    }
    case 'SET_DELIVERY_OPTION': {
      return {
        ...state,
        deliveryOption: action.deliveryOption,
      };
    }
    case 'SHOW_SPU_CART_ERROR': {
      return {
        ...state,
        showSPUCartError: action.showSPUCartError,
      };
    }
    default: {
      return { ...state };
    }
  }
};
interface CartContextProviderProps {
  children: ReactNode;
}
const CartProvider = (props: CartContextProviderProps) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [isFirstCartLoad, setIsFirstCartLoad] = useState<boolean>(true);
  const [cartLastUpdated, setCartLastUpdated] = useState<Date | null>(null);

  const { getCart, validateCart } = useCart();

  const setCartIsUpdating = () =>
    dispatch({ type: ActionType.CART_IS_UPDATING });

  const setCartDoneUpdating = () =>
    dispatch({ type: ActionType.CART_DONE_UPDATING });

  const setShowSPUCartError = useCallback((showSPUCartError: number | null) => {
    dispatch({
      type: ActionType.SHOW_SPU_CART_ERROR,
      showSPUCartError,
    });
  }, []);

  const clearCartMessaging = useCallback(() => {
    const updatedPhysicalItems =
      state.cart?.cartData?.line_items?.physical_items?.filter(
        // filter out all line items with removedMessage
        (lineItem) => !lineItem.removedMessage
      ) ?? [];
    const newCart = state.cart?.cartData
      ? {
          cartMessages: [] as CartMessage[], // wipe all cart level messages
          cartData:
            updatedPhysicalItems.length > 0 ||
            state.cart.cartData.line_items.digital_items.length > 0
              ? {
                  ...state.cart.cartData,
                  line_items: {
                    ...state.cart.cartData.line_items,
                    physical_items: updatedPhysicalItems,
                  },
                }
              : undefined,
        }
      : null;
    dispatch({ type: ActionType.FUNCTIONAL_UPDATE_CART, cart: newCart });
  }, [state.cart]);

  const updateCart = (cart: Cart | null) => {
    // reversing order of items in cart because BC provides it opposite of order we want
    let newCart: Cart = null;
    if (cart?.cartData) {
      newCart = {
        ...cart,
        cartData: {
          ...cart.cartData,
          line_items: {
            ...cart.cartData?.line_items,
            physical_items: [
              ...(cart.cartData?.line_items?.physical_items ?? []),
            ].reverse(),
            digital_items: [
              ...(cart.cartData?.line_items?.digital_items ?? []),
            ].reverse(),
          },
        },
      };
    } else if (
      !cart?.cartData &&
      cart?.cartMessages?.some(
        (m) => m.key === 'itemRemovedFromCart' || 'outOfStock'
      )
    ) {
      newCart = {
        ...cart,
      };
    }
    setCartLastUpdated(new Date());
    dispatch({ type: ActionType.UPDATE_CART, cart: newCart });
  };

  const setDeliveryOption = (deliveryOption: string) =>
    dispatch({
      type: ActionType.SET_DELIVERY_OPTION,
      deliveryOption: deliveryOption,
    });

  const totalQuantity = useMemo(() => {
    if (!state.cart || !state.cart.cartData) return 0;

    let quantity = 0;

    if (state.cart?.cartData?.line_items) {
      Object.values(state.cart?.cartData?.line_items).forEach((items) => {
        if (items.length) {
          items
            .filter(
              // filter out all line items with removedMessage
              (lineItem: CartLineItem) => !lineItem.removedMessage
            )
            .forEach((item: CartLineItem) => {
              quantity += item.quantity;
            });
        }
      });
    }

    return quantity;
  }, [state.cart]);

  const isDigitalOnly = useMemo(() => {
    if (!state.cart || !state.cart.cartData) return false;

    const physicalItems =
      state.cart.cartData.line_items?.physical_items?.filter(
        (lineItem: CartLineItem) => !lineItem.removedMessage
      ) ?? [];

    const digitalItems =
      state.cart.cartData.line_items?.digital_items?.filter(
        (lineItem: CartLineItem) => !lineItem.removedMessage
      ) ?? [];

    // Return true if there are digital items and no physical items
    return digitalItems.length > 0 && physicalItems.length === 0;
  }, [state.cart]);

  const cartShipToHomeType = useMemo(() => {
    return getCartShipToHomeType(state.cart);
  }, [state.cart]);

  useEffect(() => {
    let isCancelled = false;
    if (!state.cart && isFirstCartLoad) {
      const fetchCart = async () => {
        const cart = await getCart();
        setCartLastUpdated(new Date());

        if (cart) setIsFirstCartLoad(false);
        if (!isCancelled) {
          updateCart(cart);
        }
      };
      fetchCart();
    }

    return () => {
      isCancelled = true;
    };
  }, [getCart, state.cart, isFirstCartLoad]);

  const validateCartItems = useCallback(
    async (force = false) => {
      let shouldValidateCartItems = false;

      if (!cartLastUpdated) {
        // Validate cart items on initial fetch
        shouldValidateCartItems = true;
      } else {
        // Validate cart if it has been fetched more than 5 min ago
        const fiveMinutesAgo = new Date(new Date().getTime() - 1000 * 60 * 5);
        shouldValidateCartItems =
          cartLastUpdated?.getTime() < fiveMinutesAgo.getTime();
      }

      if (shouldValidateCartItems || force) {
        const validatedCart = await validateCart();
        updateCart(validatedCart);
      }
    },
    [validateCart, cartLastUpdated]
  );

  const value = useMemo(
    () => ({
      ...state,
      clearCartMessaging,
      setCartIsUpdating,
      setCartDoneUpdating,
      updateCart,
      validateCartItems,
      setDeliveryOption,
      totalQuantity,
      isDigitalOnly,
      cartShipToHomeType,
      setShowSPUCartError,
    }),
    [
      state,
      clearCartMessaging,
      validateCartItems,
      totalQuantity,
      isDigitalOnly,
      cartShipToHomeType,
      setShowSPUCartError,
    ]
  );

  return <CartContext.Provider value={value} {...props} />;
};

export const CartContextProvider = ({ children }: { children: ReactNode }) => (
  <CartProvider>{children}</CartProvider>
);
export const useCartUI = () => {
  const context = useContext(CartContext);

  if (context === undefined) {
    throw new Error(`useCartUI must be used within a CartProvider`);
  }

  return context;
};
